import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { 
  Plus, Edit, Trash2, GripVertical, Save, RotateCcw, ClipboardList,
  Heart, AlertCircle, Activity, Target, Brain, Scale, ChevronUp, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category configuration
const CATEGORIES = [
  { value: 'general_health', label: 'Allgemeine Gesundheit', icon: Heart, color: 'bg-red-100 text-red-700' },
  { value: 'vitals', label: 'Vitalwerte', icon: Activity, color: 'bg-rose-100 text-rose-700' },
  { value: 'body_data', label: 'Körperdaten', icon: Scale, color: 'bg-cyan-100 text-cyan-700' },
  { value: 'pain', label: 'Schmerzen', icon: AlertCircle, color: 'bg-orange-100 text-orange-700' },
  { value: 'mobility', label: 'Bewegungseinschränkungen', icon: Activity, color: 'bg-yellow-100 text-yellow-700' },
  { value: 'cardiovascular', label: 'Herz-Kreislauf', icon: Heart, color: 'bg-pink-100 text-pink-700' },
  { value: 'lifestyle', label: 'Lebensstil', icon: Brain, color: 'bg-blue-100 text-blue-700' },
  { value: 'goals', label: 'Ziele', icon: Target, color: 'bg-green-100 text-green-700' },
];

const QUESTION_TYPES = [
  { value: 'yes_no', label: 'Ja / Nein' },
  { value: 'text', label: 'Kurzer Text' },
  { value: 'textarea', label: 'Langer Text' },
  { value: 'number', label: 'Zahl' },
  { value: 'scale', label: 'Skala (1-10)' },
  { value: 'single_choice', label: 'Einzelauswahl' },
  { value: 'multi_choice', label: 'Mehrfachauswahl' },
  { value: 'date', label: 'Datum' },
];

const AnamneseSettings = () => {
  const [template, setTemplate] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Question editor dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    category: 'general_health',
    question_text: '',
    question_type: 'yes_no',
    options: [],
    required: true,
    help_text: ''
  });
  const [optionsText, setOptionsText] = useState('');
  
  // Filter by category
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/anamnese/templates/default`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplate(response.data);
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Error fetching template:', error);
      toast.error('Fehler beim Laden der Anamnese-Vorlage');
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Reassign order based on current position
      const orderedQuestions = questions.map((q, idx) => ({
        ...q,
        order: idx + 1
      }));
      
      await axios.put(`${API}/anamnese/templates/default`, {
        questions: orderedQuestions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Anamnese-Vorlage gespeichert');
      setHasChanges(false);
      setQuestions(orderedQuestions);
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const resetTemplate = async () => {
    if (!window.confirm('Vorlage wirklich auf Standard zurücksetzen? Alle Anpassungen gehen verloren.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/anamnese/templates/default/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setQuestions(response.data.questions || []);
      setHasChanges(false);
      toast.success('Vorlage auf Standard zurückgesetzt');
    } catch (error) {
      console.error('Error resetting template:', error);
      toast.error('Fehler beim Zurücksetzen');
    }
  };

  const openNewQuestion = () => {
    setEditingQuestion(null);
    setQuestionForm({
      category: selectedCategory !== 'all' ? selectedCategory : 'general_health',
      question_text: '',
      question_type: 'yes_no',
      options: [],
      required: true,
      help_text: ''
    });
    setOptionsText('');
    setDialogOpen(true);
  };

  const openEditQuestion = (question) => {
    setEditingQuestion(question);
    setQuestionForm({
      category: question.category,
      question_text: question.question_text,
      question_type: question.question_type,
      options: question.options || [],
      required: question.required !== false,
      help_text: question.help_text || ''
    });
    setOptionsText((question.options || []).join('\n'));
    setDialogOpen(true);
  };

  const saveQuestion = () => {
    // Validate
    if (!questionForm.question_text.trim()) {
      toast.error('Bitte Fragetext eingeben');
      return;
    }
    
    // Parse options if needed
    let options = null;
    if (['single_choice', 'multi_choice'].includes(questionForm.question_type)) {
      options = optionsText.split('\n').map(o => o.trim()).filter(o => o);
      if (options.length < 2) {
        toast.error('Mindestens 2 Optionen erforderlich');
        return;
      }
    }
    
    const questionData = {
      id: editingQuestion?.id || `q_${Date.now()}`,
      category: questionForm.category,
      question_text: questionForm.question_text.trim(),
      question_type: questionForm.question_type,
      options: options,
      required: questionForm.required,
      help_text: questionForm.help_text.trim() || null,
      order: editingQuestion?.order || questions.length + 1
    };
    
    if (editingQuestion) {
      // Update existing
      setQuestions(prev => prev.map(q => q.id === editingQuestion.id ? questionData : q));
    } else {
      // Add new
      setQuestions(prev => [...prev, questionData]);
    }
    
    setHasChanges(true);
    setDialogOpen(false);
    toast.success(editingQuestion ? 'Frage aktualisiert' : 'Frage hinzugefügt');
  };

  const deleteQuestion = (questionId) => {
    if (!window.confirm('Frage wirklich löschen?')) return;
    
    setQuestions(prev => prev.filter(q => q.id !== questionId));
    setHasChanges(true);
    toast.success('Frage gelöscht');
  };

  const moveQuestion = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= questions.length) return;
    
    const newQuestions = [...questions];
    [newQuestions[index], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[index]];
    setQuestions(newQuestions);
    setHasChanges(true);
  };

  const toggleRequired = (questionId) => {
    setQuestions(prev => prev.map(q => 
      q.id === questionId ? { ...q, required: !q.required } : q
    ));
    setHasChanges(true);
  };

  // Filter questions by category
  const filteredQuestions = selectedCategory === 'all' 
    ? questions 
    : questions.filter(q => q.category === selectedCategory);

  // Group questions by category for display
  const questionsByCategory = CATEGORIES.reduce((acc, cat) => {
    acc[cat.value] = questions.filter(q => q.category === cat.value);
    return acc;
  }, {});

  const getCategoryConfig = (categoryValue) => {
    return CATEGORIES.find(c => c.value === categoryValue) || CATEGORIES[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Anamnese-Fragen verwalten
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {questions.length} Fragen in {CATEGORIES.length} Kategorien
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetTemplate}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Zurücksetzen
          </Button>
          <Button onClick={saveTemplate} disabled={!hasChanges || saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Speichern...' : 'Speichern'}
          </Button>
        </div>
      </div>

      {hasChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
          ⚠️ Sie haben ungespeicherte Änderungen. Klicken Sie auf "Speichern" um die Änderungen zu übernehmen.
        </div>
      )}

      {/* Category Filter + Add Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label>Kategorie:</Label>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kategorien</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label} ({questionsByCategory[cat.value]?.length || 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={openNewQuestion}>
          <Plus className="h-4 w-4 mr-2" />
          Neue Frage
        </Button>
      </div>

      {/* Questions List */}
      <Card>
        <CardContent className="p-4">
          {filteredQuestions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ClipboardList className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Keine Fragen in dieser Kategorie.</p>
              <Button variant="link" onClick={openNewQuestion}>
                Erste Frage hinzufügen
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredQuestions.map((question, index) => {
                const catConfig = getCategoryConfig(question.category);
                const Icon = catConfig.icon;
                
                return (
                  <div 
                    key={question.id}
                    className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50 group"
                  >
                    {/* Drag Handle / Move Buttons */}
                    <div className="flex flex-col gap-1 pt-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0"
                        onClick={() => moveQuestion(questions.indexOf(question), -1)}
                        disabled={questions.indexOf(question) === 0}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0"
                        onClick={() => moveQuestion(questions.indexOf(question), 1)}
                        disabled={questions.indexOf(question) === questions.length - 1}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* Question Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`${catConfig.color} text-xs`}>
                          <Icon className="h-3 w-3 mr-1" />
                          {catConfig.label}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {QUESTION_TYPES.find(t => t.value === question.question_type)?.label || question.question_type}
                        </Badge>
                        {question.required && (
                          <Badge variant="destructive" className="text-xs">Pflicht</Badge>
                        )}
                      </div>
                      <p className="font-medium">{question.question_text}</p>
                      {question.help_text && (
                        <p className="text-sm text-gray-500 mt-1">{question.help_text}</p>
                      )}
                      {question.options && question.options.length > 0 && (
                        <p className="text-sm text-gray-400 mt-1">
                          Optionen: {question.options.join(', ')}
                        </p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => toggleRequired(question.id)}
                        title={question.required ? 'Als optional markieren' : 'Als Pflichtfeld markieren'}
                      >
                        {question.required ? '⭐' : '☆'}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditQuestion(question)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700"
                        onClick={() => deleteQuestion(question.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Category Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Übersicht nach Kategorien</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {CATEGORIES.map(cat => {
              const count = questionsByCategory[cat.value]?.length || 0;
              const Icon = cat.icon;
              return (
                <div 
                  key={cat.value}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    selectedCategory === cat.value ? 'ring-2 ring-blue-500' : ''
                  } ${cat.color}`}
                  onClick={() => setSelectedCategory(cat.value)}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <span className="font-medium text-sm">{cat.label}</span>
                  </div>
                  <div className="text-2xl font-bold mt-1">{count}</div>
                  <div className="text-xs opacity-75">Fragen</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Question Editor Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingQuestion ? 'Frage bearbeiten' : 'Neue Frage erstellen'}
            </DialogTitle>
            <DialogDescription>
              {editingQuestion ? 'Bearbeiten Sie die Anamnese-Frage' : 'Fügen Sie eine neue Frage zum Anamnese-Fragebogen hinzu'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Category */}
            <div>
              <Label>Kategorie</Label>
              <Select 
                value={questionForm.category} 
                onValueChange={(v) => setQuestionForm(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Question Text */}
            <div>
              <Label>Fragetext *</Label>
              <Textarea
                value={questionForm.question_text}
                onChange={(e) => setQuestionForm(prev => ({ ...prev, question_text: e.target.value }))}
                placeholder="Z.B. Haben Sie aktuell Schmerzen?"
                rows={2}
              />
            </div>
            
            {/* Question Type */}
            <div>
              <Label>Fragetyp</Label>
              <Select 
                value={questionForm.question_type} 
                onValueChange={(v) => setQuestionForm(prev => ({ ...prev, question_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUESTION_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Options (for choice questions) */}
            {['single_choice', 'multi_choice'].includes(questionForm.question_type) && (
              <div>
                <Label>Antwortoptionen (eine pro Zeile)</Label>
                <Textarea
                  value={optionsText}
                  onChange={(e) => setOptionsText(e.target.value)}
                  placeholder={"Option 1\nOption 2\nOption 3"}
                  rows={4}
                />
              </div>
            )}
            
            {/* Help Text */}
            <div>
              <Label>Hilfetext (optional)</Label>
              <Input
                value={questionForm.help_text}
                onChange={(e) => setQuestionForm(prev => ({ ...prev, help_text: e.target.value }))}
                placeholder="Z.B. Bitte beschreiben Sie die Schmerzen genauer"
              />
            </div>
            
            {/* Required */}
            <div className="flex items-center justify-between">
              <Label>Pflichtfeld</Label>
              <Switch
                checked={questionForm.required}
                onCheckedChange={(v) => setQuestionForm(prev => ({ ...prev, required: v }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={saveQuestion}>
              {editingQuestion ? 'Aktualisieren' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnamneseSettings;
